import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { useForm } from 'react-hook-form';
import AuthLayout from '../components/Auth/AuthLayout';
import { Button } from '../components/UI/button';
import { Input } from '../components/UI/input';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';
import { colors } from '../config/colors';

const ForgotPassword = () => {
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const continueUrl = typeof window !== 'undefined' ? `${window.location.origin}/login` : null;
      await authService.forgotPassword(data.email, continueUrl);
      setSent(true);
      // Always show the same generic message for security (don't reveal if email exists).
      toast.success(t('authForgotPasswordSuccess'));
    } catch (_) {
      setSent(true);
      // Still show generic success message to avoid leaking "user not found".
      toast.success(t('authForgotPasswordSuccess'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="flex flex-1 flex-col gap-10 justify-center min-h-0 w-full">
        <div className="flex flex-col gap-3 shrink-0 w-full">
          <Link
            to="/login"
            className="inline-flex gap-2 items-center font-medium text-base w-fit hover:underline"
            style={{ color: colors.text.accent }}
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            {t('authBackToLogin')}
          </Link>
          <h1
            className="font-semibold text-[1.875rem] leading-9 tracking-tight shrink-0 w-full"
            style={{ color: colors.text.primary }}
          >
            {t('authForgotPasswordTitle')}
          </h1>
          <p
            className="font-normal text-base shrink-0 w-full"
            style={{ color: colors.text.secondary }}
          >
            {t('authForgotPasswordDescription')}
          </p>
        </div>

        {sent ? (
          <div
            className="rounded-lg p-4 text-sm"
            style={{
              backgroundColor: colors.success[50],
              color: colors.success[800],
            }}
          >
            {t('authForgotPasswordSuccess')}
          </div>
        ) : (
          <form className="flex flex-col gap-6 shrink-0 w-full" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-1.5 w-full">
              <label
                htmlFor="email"
                className="font-medium text-sm leading-5"
                style={{ color: colors.text.primary }}
              >
                {t('authEmailLabel')}
              </label>
              <Input
                {...register('email', {
                  required: true,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email',
                  },
                })}
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t('authEmailPlaceholder')}
              />
              {errors.email && (
                <p className="text-sm" style={{ color: colors.danger[500] }}>
                  {errors.email.message === 'Invalid email' ? t('authValidationInvalidEmail') : t('authValidationRequired')}
                </p>
              )}
            </div>

            <Button type="submit" disabled={loading} className="w-full" variant="pill" size="pill">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="sm" />
                  {t('authSendingLink')}
                </span>
              ) : (
                t('authSendResetLink')
              )}
            </Button>
          </form>
        )}
      </div>

      <div className="flex flex-col gap-1 items-center shrink-0 w-full mt-auto pt-10">
        <p
          className="font-normal text-base text-center w-full"
          style={{ color: colors.text.secondary }}
        >
          {t('authTermsPrefix')}{' '}
          <Link
            to="/settings"
            className="font-medium text-base hover:underline"
            style={{ color: colors.text.accent }}
          >
            {t('authTermsAndPrivacy')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
